import { useNavigate } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { AvailableCurrencyFragment, PaymentsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { NewPaymentModal } from 'modules/banking/components/NewPaymentModal'
import { PaymentsDataCollection } from 'modules/banking/components/PaymentsDataCollection'
import { PaymentsDataTable } from 'modules/banking/components/PaymentsDataTable'
import { AllPaymentsInsightCards } from 'modules/banking/components/TreasuryInsightCard'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const FEATURE_FLAG_NAME = 'dev_payments_enable_datacollection'

const PaymentsPage = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const currency = company.legalEntities.find((el) => el.main)?.currency ?? 'EUR'

  const { data, loading, error } = useQuery(PaymentsPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: ['dev_finance_swan_reactivation', FEATURE_FLAG_NAME],
    },
  })

  const canSeeSepaTransferAction =
    data?.features?.featuresConnection?.nodes.find(
      (feature) => feature.name === 'dev_finance_swan_reactivation'
    )?.enabled ?? false

  const isDataCollectionEnabled =
    data?.features?.featuresConnection?.nodes.find((feature) => feature.name === FEATURE_FLAG_NAME)
      ?.enabled ?? false

  const availableCurrencies: AvailableCurrencyFragment[] =
    data?.apiCore?.currenciesConnection?.edges?.map((edge) => edge.node) ?? []

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [newPaymentModalOpen, toggleNewPaymentModal] = useToggle(false)

  const outstandingTransfersCount = data?.banking.transfersConnection?.totalCount || 0

  const handleOnCreationSuccess = (newPaymentId: number) => {
    toggleNewPaymentModal(false)()

    if (outstandingTransfersCount > 0) {
      navigate(
        navigation.finance.banking.payments.transfers.fullPath() + '?paymentId=' + newPaymentId
      )
    }
  }

  const canSeePaymentsModule =
    !!data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can

  useUnsafeEffect(() => {
    if (!loading) {
      const paymentsCount = data?.banking.paymentsConnection?.aggregate?.count || 0
      const paymentsStatuses =
        data?.banking.paymentsConnection?.aggregate?.terms?.status?.values || []

      const user_making_payment = canSeePaymentsModule ? 'yes' : 'no'

      track(trackingEvents.accounting.payments.sectionBulkPayments, {
        user_making_payment: user_making_payment,
        has_data: paymentsCount > 0,
        bulk_payments_count: paymentsCount,
        bulk_payments_status: paymentsStatuses,
      })
    }
  }, [canSeePaymentsModule, data, loading])

  return (
    <ResponsivePage.Body
      error={error}
      loading={loading}
      authorized={canSeePaymentsModule}
      variant="full"
    >
      <Box flexDirection="column" gap="s16" width="full" height="full">
        {isDataCollectionEnabled ? (
          <>
            <AllPaymentsInsightCards currency={currency} locale={locale} />
            <PaymentsDataCollection
              companyId={company.id}
              locale={locale}
              canSeeSepaTransferAction={canSeeSepaTransferAction}
              onNewPaymentAction={toggleNewPaymentModal(true)}
            />
          </>
        ) : (
          <PaymentsDataTable
            onNewPaymentAction={toggleNewPaymentModal(true)}
            locale={locale}
            currency={currency}
            availableCurrencies={availableCurrencies}
            canSeeSepaTransferAction={canSeeSepaTransferAction}
          />
        )}
        {newPaymentModalOpen && (
          <NewPaymentModal
            onSuccess={handleOnCreationSuccess}
            onClose={toggleNewPaymentModal(false)}
          />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default PaymentsPage
